// The pieces every screen is built from. Deliberately small and unstyled-by-
// default so a page composes them rather than inventing its own chrome.

import { type ReactNode, type ButtonHTMLAttributes, type InputHTMLAttributes,
         type SelectHTMLAttributes, useEffect, useRef } from 'react';

export const cx = (...xs: (string | false | null | undefined)[]) => xs.filter(Boolean).join(' ');

// ── page furniture ────────────────────────────────────────────
export function PageHead(
  { eyebrow, title, dek, actions }:
  { eyebrow?: ReactNode; title: string; dek?: ReactNode; actions?: ReactNode },
) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-4 mb-6">
      <div className="min-w-0">
        {eyebrow && (
          <p className="font-mono text-[10.5px] uppercase tracking-[0.13em] text-muted m-0">{eyebrow}</p>
        )}
        <h1 className="font-display text-[27px] font-bold tracking-[-0.02em] leading-tight mt-1 text-balance">
          {title}
        </h1>
        {dek && <p className="mt-2 text-[13.5px] text-ink-2 max-w-[72ch]">{dek}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 no-print">{actions}</div>}
    </div>
  );
}

export function Card(
  { title, sub, actions, children, flush, className }:
  { title?: string; sub?: ReactNode; actions?: ReactNode; children: ReactNode;
    flush?: boolean; className?: string },
) {
  return (
    <section className={cx('bg-card border border-rule rounded shadow-sm', flush ? '' : 'p-5', className)}>
      {(title || actions) && (
        <div className={cx('flex flex-wrap items-baseline justify-between gap-3 mb-3.5', flush && 'p-5 pb-0')}>
          <div className="min-w-0">
            {title && <h2 className="font-display text-base font-semibold tracking-[-0.01em] m-0">{title}</h2>}
            {sub && <p className="mt-1.5 text-[12.5px] text-ink-2 max-w-[70ch]">{sub}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 no-print">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** A row of stat tiles. The first may be a hero when the figure is the point. */
export function Tiles({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-rule border border-rule rounded overflow-hidden mb-6">
      {children}
    </div>
  );
}

export function Tile(
  { label, value, sub, hero }:
  { label: string; value: ReactNode; sub?: ReactNode; hero?: boolean },
) {
  // The hero is larger in type, not in span: spanning two of two columns on a
  // tablet leaves a hole in the grid where a tile should be.
  return (
    <div className="bg-card p-4 pb-5 min-w-0">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted m-0 mb-2">{label}</p>
      {/* Proportional figures on a large standalone number — tabular makes 121 look loose. */}
      <div className={cx('font-display font-semibold tracking-[-0.02em] leading-none',
        hero ? 'text-[42px] tracking-[-0.03em]' : 'text-[25px]')}>{value}</div>
      {sub && <p className="mt-2 text-[12.5px] text-ink-2 leading-snug">{sub}</p>}
    </div>
  );
}

export const Muted = ({ children }: { children: ReactNode }) =>
  <span className="text-muted">{children}</span>;

// ── controls ──────────────────────────────────────────────────
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'ghost'; size?: 'sm' | 'md'; block?: boolean;
};
export function Button({ variant = 'default', size = 'md', block, className, ...rest }: BtnProps) {
  return (
    <button
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded border font-medium cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-s1',
        // 44px on touch for anything tappable, including the secondary sizes;
        // desktop returns to the denser original.
        size === 'sm'
          ? 'text-xs px-3 py-1.5 min-h-[44px] md:px-2.5 md:min-h-0'
          : 'text-[14px] md:text-[13px] px-3.5 py-2.5 min-h-[44px] md:min-h-0',
        variant === 'primary' && 'bg-brand border-brand text-brand-ink font-semibold hover:brightness-95',
        variant === 'default' && 'bg-card border-rule text-ink hover:bg-card-2',
        variant === 'ghost' && 'bg-transparent border-transparent text-ink-2 hover:bg-card-2 hover:text-ink',
        block && 'w-full',
        className,
      )}
    />
  );
}

// 16px on small screens is not a style choice: iOS Safari zooms the whole page
// when a focused field is any smaller, which throws the layout sideways
// mid-typing. Desktop tightens back to the denser 13.5px. The 44px minimum
// height is what a finger reliably hits.
const fieldCls =
  'w-full rounded border border-rule bg-card px-3 py-2.5 min-h-[44px] text-[16px] text-ink ' +
  'md:px-2.5 md:py-2 md:min-h-0 md:text-[13.5px] ' +
  'focus:outline-2 focus:-outline-offset-1 focus:outline-s1';

export const Input = (p: InputHTMLAttributes<HTMLInputElement>) =>
  <input {...p} className={cx(fieldCls, p.type === 'number' && 'tnum', p.className)} />;

export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) =>
  <select {...p} className={cx(fieldCls, p.className)} />;

export function Field({ label, children, span }: { label: string; children: ReactNode; span?: boolean }) {
  return (
    <label className={cx('flex flex-col gap-1.5 min-w-0', span && 'sm:col-span-2')}>
      <span className="font-mono text-[10.5px] uppercase tracking-[0.09em] text-muted">{label}</span>
      {children}
    </label>
  );
}

export const FormGrid = ({ children }: { children: ReactNode }) =>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">{children}</div>;

// ── status ────────────────────────────────────────────────────
const PILL: Record<string, string> = {
  paid: 'text-good', sent: 'text-s1', draft: 'text-muted',
  part_paid: 'text-warning', overdue: 'text-critical', cancelled: 'text-critical',
  credit_note: 'text-critical', submitted: 'text-s1', approved: 'text-warning',
  rejected: 'text-critical', active: 'text-s1',
};

export function Pill({ kind, children }: { kind: string; children: ReactNode }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 rounded px-2 py-0.5 bg-card-2',
      'font-mono text-[11px] whitespace-nowrap', PILL[kind] ?? 'text-ink-2')}>
      <i className="w-1.5 h-1.5 rounded-full bg-current shrink-0" aria-hidden />
      {children}
    </span>
  );
}

export const Tag = ({ children, tone }: { children: ReactNode; tone?: 'brand' }) => (
  <span className={cx('inline-block rounded px-1.5 py-0.5 font-mono text-[10.5px]',
    tone === 'brand' ? 'bg-brand text-brand-ink' : 'bg-card-2 text-ink-2')}>{children}</span>
);

/** A callout. `tone` says how alarmed to be; the left rule carries it. */
export function Note(
  { title, tone, children }:
  { title: string; tone?: 'critical' | 'good'; children: ReactNode },
) {
  return (
    <div className={cx('bg-card border border-rule border-l-[3px] rounded p-4 mb-4',
      tone === 'critical' ? 'border-l-critical' : tone === 'good' ? 'border-l-good' : 'border-l-brand')}>
      <h4 className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted m-0 mb-1.5">{title}</h4>
      <div className="text-[12.5px] text-ink-2 leading-relaxed">{children}</div>
    </div>
  );
}

// ── tables ────────────────────────────────────────────────────
export const TableWrap = ({ children, scroll }: { children: ReactNode; scroll?: boolean }) => (
  <div className={cx('overflow-x-auto', scroll && 'max-h-[520px] overflow-y-auto')}>{children}</div>
);

/**
 * `cards` turns the table into a stack of cards below sm — see index.css. Use
 * it for lists of records; leave it off for a report grid, where the shape of
 * the table is itself the information.
 */
export const Table = ({ children, cards }: { children: ReactNode; cards?: boolean }) =>
  <table data-cards={cards ? '' : undefined}
         className="w-full border-collapse text-[13px]">{children}</table>;

export const Th = ({ children, n }: { children?: ReactNode; n?: boolean }) => (
  <th className={cx('sticky top-0 z-10 bg-card border-b border-rule px-3 py-2.5',
    'font-mono text-[10.5px] font-normal uppercase tracking-[0.09em] text-muted whitespace-nowrap',
    n ? 'text-right' : 'text-left')}>{children}</th>
);

/** `label` is the column name, shown beside the value once the table stacks. */
export const Td = (
  { children, n, className, label }:
  { children?: ReactNode; n?: boolean; className?: string; label?: string },
) => (
  <td data-label={label} className={cx('border-b border-rule-soft px-3 py-2.5 align-top',
    n && 'text-right tnum whitespace-nowrap', className)}>{children}</td>
);

export const Empty = ({ children }: { children: ReactNode }) =>
  <div className="px-5 py-9 text-center text-[13.5px] text-muted">{children}</div>;

// ── modal ─────────────────────────────────────────────────────
export function Modal(
  { title, children, onClose, onConfirm, confirmLabel = 'Save', busy, wide }:
  { title: string; children: ReactNode; onClose: () => void;
    onConfirm?: () => void; confirmLabel?: string; busy?: boolean; wide?: boolean },
) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    box.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus();
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-6 no-print"
         onClick={onClose} role="presentation">
      <div ref={box} role="dialog" aria-modal="true" aria-label={title}
           onClick={(e) => e.stopPropagation()}
           className={cx('bg-card border border-rule rounded-md shadow-2xl w-full max-h-[86vh] overflow-auto p-6',
             wide ? 'max-w-[760px]' : 'max-w-[560px]')}>
        <h3 className="font-display text-lg font-semibold m-0 mb-4">{title}</h3>
        {children}
        {onConfirm && (
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={onConfirm} disabled={busy}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
